import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { SurchargeTypeRepository } from "../../surcharge-type/surcharge-type.repository";
import { SurchargeType } from "../../surcharge-type/surcharge-type.model";
import { Rate } from "../../rate/rate-type";
import { BaseLclQuote } from "../models/quote-base-lcl.model";
import { QuoteRepository } from "../quote.repository";
import { QuoteSurchargeType } from "../models/quote-surcharge-type.model";
import { eventType } from "app/notify/event.const";
import { EventService } from "app/shared/event/event.service";
import { UserService } from "app/user/user.service";
import { languageToLocale } from "app/shared/language/language-to-locale.function";
import { Tooltip } from "primeng/tooltip";
import { Checkbox } from "primeng/checkbox";
import { FormsModule } from "@angular/forms";
import { Popover } from "primeng/popover";
import { PopoverArrowDirective } from "app/ui/popover/popover-arrow.directive";
import { SafeHtmlPipe } from "app/shared/html/safe-html.pipe";

@Component({
  selector: "app-quote-ltl-surcharge",
  templateUrl: "./quote-ltl-surcharge.component.html",
  styleUrls: ["./quote-ltl-surcharge.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Tooltip,
    Checkbox,
    FormsModule,
    Popover,
    PopoverArrowDirective,
    SafeHtmlPipe,
  ],
})
export class QuoteLtlSurchargeComponent implements OnInit, OnChanges {
  private surchargeTypeRepository = inject(SurchargeTypeRepository);
  private quoteRepository = inject(QuoteRepository);
  private eventService = inject(EventService);
  private userService = inject(UserService);

  public readonly quote = input<BaseLclQuote>(undefined);
  public readonly rates = input<Rate[]>(undefined);
  public readonly selectionChange = output();
  // Fires on the click, before the save: the rates it reprices start loading now.
  public readonly selectionChangeStart = output();

  public subscription = new Subscription();
  public quoteSurchargeTypes: QuoteSurchargeType[] = [];
  public surchargeTypeList = []; // rateId => SelectItem[];
  private surchargeTypeListQuoteId: number;
  public loading = false;
  public locale = "";
  public warningMessage = "";

  ngOnInit() {
    this.userService.getUser().then((user) => {
      this.locale = languageToLocale(user.getLanguage()) ?? "";
      this.sortLoadedSurchargeTypes();
      this.buildSurchargeTypeList();
      this.buildSurchargeTypes();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!!changes.quote || !!changes.rates) {
      this.buildSurchargeTypeList();
      this.buildSurchargeTypes();
    }
  }

  // Package changes decide which surcharges fail their restrictions, so a
  // quote update re-fetches the lists already shown.
  public refresh() {
    this.buildSurchargeTypeList(true);
  }

  public openWarning(
    event: Event,
    surchargeType: SurchargeType,
    popover: Popover,
  ): void {
    this.warningMessage = surchargeType.getWarningMessage();
    popover.toggle(event);
  }

  public toggleSelection(rate, surchargeType) {
    if (this.loading || surchargeType.getDeleted()) {
      return false;
    }

    if (this.isSelected(rate, surchargeType)) {
      for (let i = 0; i < this.quoteSurchargeTypes.length; i++) {
        if (
          this.quoteSurchargeTypes[i].rate.getId() === rate.getId() &&
          this.quoteSurchargeTypes[i].surchargeTypeId === surchargeType.getId()
        ) {
          this.loading = true;
          this.selectionChangeStart.emit();
          this.quoteRepository
            .deleteQuoteSurchargeType(this.quoteSurchargeTypes[i])
            .subscribe({
              next: () => {
                this.quoteSurchargeTypes.splice(i, 1);
                this.quote().setQuoteSurchargeTypes(this.quoteSurchargeTypes);
                this.onSelectionSaved();
              },
              error: () => this.onSelectionFailed(),
            });
        }
      }
    } else {
      this.loading = true;
      this.selectionChangeStart.emit();
      this.quoteRepository
        .createQuoteSurchargeType(this.quote(), rate, surchargeType)
        .subscribe({
          next: (quoteSurchargeType) => {
            this.quoteSurchargeTypes.push(quoteSurchargeType);
            this.quote().setQuoteSurchargeTypes(this.quoteSurchargeTypes);
            this.onSelectionSaved();
          },
          error: () => this.onSelectionFailed(),
        });
    }
  }

  private onSelectionSaved(): void {
    this.selectionChange.emit();
    this.eventService.eventBus.emit({
      event: eventType.quoteAverageMarginUpdated,
    });
    this.loading = false;
  }

  // The rates already show the loader from the click; reloading them clears it
  // and shows the selection that actually got saved.
  private onSelectionFailed(): void {
    this.selectionChange.emit();
    this.loading = false;
  }

  public isSelected(rate, surchargeType) {
    if (surchargeType.getDeleted()) {
      return surchargeType.getSelected() === true;
    }

    for (let i = 0; i < this.quoteSurchargeTypes.length; i++) {
      if (
        this.quoteSurchargeTypes[i].rate.getId() === rate.getId() &&
        this.quoteSurchargeTypes[i].surchargeTypeId === surchargeType.getId()
      ) {
        return true;
      }
    }

    return false;
  }

  public buildSurchargeTypes() {
    this.quoteSurchargeTypes = this.quote().getQuoteSurchargeTypes();
  }

  private buildSurchargeTypeList(reload = false): void {
    const rates = this.rates();
    if (!rates) {
      return;
    }

    const quoteId = this.quote()?.getId();
    if (quoteId !== this.surchargeTypeListQuoteId) {
      this.surchargeTypeList = [];
      this.surchargeTypeListQuoteId = quoteId;
    }

    const surchargeTypeList = this.surchargeTypeList;
    for (let i = 0; i < rates.length; i++) {
      const rateId = rates[i].getId();
      const loaded = typeof surchargeTypeList[rateId] !== "undefined";
      if (!loaded || reload) {
        surchargeTypeList[rateId] ??= [];
        this.subscription.add(
          this.surchargeTypeRepository
            .listByRate(rates[i], quoteId)
            .subscribe((typeList) => {
              surchargeTypeList[rateId] = this.sortSurchargeTypes(typeList);
            }),
        );
      }
    }
  }

  // The list can be built from ngOnChanges before getUser() resolves the
  // locale, so re-sort what is already loaded once it is known.
  private sortLoadedSurchargeTypes(): void {
    for (const rateId of Object.keys(this.surchargeTypeList)) {
      this.surchargeTypeList[rateId] = this.sortSurchargeTypes(
        this.surchargeTypeList[rateId],
      );
    }
  }

  // A comparator failure must never cost the user the whole list — show the
  // surcharges unsorted rather than nothing.
  private sortSurchargeTypes(typeList: SurchargeType[]): SurchargeType[] {
    try {
      return [...typeList].sort((a, b) => this.compareByGroupThenName(a, b));
    } catch (error) {
      console.error("Sorting surcharge types failed", error);

      return [...typeList];
    }
  }

  private compareByGroupThenName(a: SurchargeType, b: SurchargeType): number {
    const aMandatory = a.getMandatory() ? 1 : 0;
    const bMandatory = b.getMandatory() ? 1 : 0;

    if (aMandatory !== bMandatory) {
      return aMandatory - bMandatory;
    }

    // Users without a language get the main name and the runtime default
    // collation: localeCompare() throws on null, only undefined means "default".
    const locale = this.locale || undefined;

    return a
      .getNameForLocale(this.locale)
      .localeCompare(b.getNameForLocale(this.locale), locale);
  }
}
